/*
 * // Copyright (c) 2024 OPEN CASCADE SAS
 * //
 * // This file is part of commercial software by OPEN CASCADE SAS,
 * // furnished in accordance with the terms and conditions of the contract
 * // and with the inclusion of this copyright notice.
 * // This file or any part thereof may not be provided or otherwise
 * // made available to any third party.
 * //
 * // No ownership title to the software is transferred hereby.
 * //
 * // OPEN CASCADE SAS makes no representation or warranties with respect to the
 * // performance of this software, and specifically disclaims any responsibility
 * // for any damages, special or consequential, connected with its use.
 *
 */
import { Mat4Tools } from "../../../core/geometry/mat4-tools.js";
import { OperationSide } from "./patient-info.js";
import { Vec3Tools } from "../../../core/geometry/vec3-tools.js";
import { PatientUtils } from "./patient-utils.js";
import { StemHeadUtilities } from "./stem-head-utilities.js";
export class PatientContextWriter {
    /**
     * Writes patient context into JSON.
     * The function updates theJson according to theContext.
     * If theJson is not valid, a new JSON will be created.
     * @param theContext - Patient context.
     * @param theJson - Output JSON object.
     */
    static writePatientJson(theContext, theJson) {
        const aUnitsScale = theContext.engineering.unitsScale;
        const aSystemTransform = Mat4Tools.invert(theContext.engineering.rotationMatrix);
        if (!theJson || typeof theJson !== 'object') {
            theJson = {
                "patientInformation": null,
                "surgeonInformation": null,
                "procedureInformation": null,
                "caseInformation": null,
                "implantDetails": null,
                "planDetails": null,
                "rangeOfMotion": null,
                "percentageOfImpingement": null,
                "transforms": null,
                "landmarks": null,
                "referenceNotes": null,
            };
        }
        //! Transform transformations
        const aTrans = (theMat) => {
            if (theMat) {
                let aTransform = Mat4Tools.multiply(aSystemTransform, theMat);
                aTransform = Mat4Tools.scaleUnits(aTransform, 1.0 / aUnitsScale);
                return Mat4Tools.transpose(aTransform);
            }
            return Mat4Tools.identity();
        };
        const aTransforms = theContext.current.transform;
        const anOperationSide = theContext.engineering.operationSide;
        //! Write transformations
        theJson.transforms = {
            pelvisTGlobal: aTrans(aTransforms.pelvisTGlobal),
            femurLeftTGlobal: aTrans(aTransforms.femurLeftTGlobal),
            femurRightTGlobal: aTrans(aTransforms.femurRightTGlobal),
            stemTGlobal: aTrans(Mat4Tools.multiply(aTransforms.stemTGlobal, Mat4Tools.scale(1.0 / theContext.engineering.stemInfo.scale))),
            cupTGlobal: aTrans(Mat4Tools.multiply(aTransforms.shellTGlobal, Mat4Tools.scale(1.0 / theContext.engineering.shellInfo.scale))),
            femurLeftPreopTGlobal: anOperationSide == OperationSide.Left ? aTrans(aTransforms.femurLeftPreopTGlobal) : null,
            femurRightPreopTGlobal: anOperationSide == OperationSide.Right ? aTrans(aTransforms.femurRightPreopTGlobal) : null,
        };
        //! Process landmarks
        const aPL = (theLandmark) => {
            if (theLandmark && !Vec3Tools.isInfinite(theLandmark)) {
                var aPnt = Vec3Tools.applyMatrix(aSystemTransform, theLandmark);
                aPnt = Vec3Tools.multiply(aPnt, 1.0 / aUnitsScale);
                return [aPnt.x, aPnt.y, aPnt.z];
            }
            return null;
        };
        const aLandmarks = theContext.current.landmarks;
        //! Write landmarks
        theJson.landmarks = {
            pelvisLeftCorLandmark: aPL(aLandmarks.pelvisLeftCorLandmark),
            pelvisRightCorLandmark: aPL(aLandmarks.pelvisRightCorLandmark),
            pelvisLeftAsisLandmark: aPL(aLandmarks.pelvisLeftAsisLandmark),
            pelvisRightAsisLandmark: aPL(aLandmarks.pelvisRightAsisLandmark),
            pelvisLeftPubicSymphysisLandmark: aPL(aLandmarks.pelvisLeftPubicSymphysisLandmark),
            pelvisRightPubicSymphysisLandmark: aPL(aLandmarks.pelvisRightPubicSymphysisLandmark),
            femurLeftCorLandmark: aPL(aLandmarks.femurLeftCorLandmark),
            femurRightCorLandmark: aPL(aLandmarks.femurRightCorLandmark),
            femurLeftLtLandmark: aPL(aLandmarks.femurLeftLtLandmark),
            femurRightLtLandmark: aPL(aLandmarks.femurRightLtLandmark),
            femurLeftLtProxLandmark: aPL(aLandmarks.femurLeftLtProxLandmark),
            femurRightLtProxLandmark: aPL(aLandmarks.femurRightLtProxLandmark),
            femurLeftGtLandmark: aPL(aLandmarks.femurLeftGtLandmark),
            femurRightGtLandmark: aPL(aLandmarks.femurRightGtLandmark),
            femurLeftCondyleMedLandmark: aPL(aLandmarks.femurLeftCondyleMedLandmark),
            femurLeftCondyleLatLandmark: aPL(aLandmarks.femurLeftCondyleLatLandmark),
            femurRightCondyleMedLandmark: aPL(aLandmarks.femurRightCondyleMedLandmark),
            femurRightCondyleLatLandmark: aPL(aLandmarks.femurRightCondyleLatLandmark),
            femurLeftAxisStartLandmark: aPL(aLandmarks.femurLeftAxisStartLandmark),
            femurLeftAxisEndLandmark: aPL(aLandmarks.femurLeftAxisEndLandmark),
            femurRightAxisStartLandmark: aPL(aLandmarks.femurRightAxisStartLandmark),
            femurRightAxisEndLandmark: aPL(aLandmarks.femurRightAxisEndLandmark),
        };
        //! Write plan details
        const aPatientUtils = new PatientUtils(theContext);
        const aPlanDetails = theContext.engineering.planDetails;
        theJson.planDetails = {
            spmClassification: aPlanDetails.spmClassification,
            pelvicTilt: aPatientUtils.pelvisTilt * 180.0 / Math.PI,
            sittingSacralSlope: aPlanDetails.sittingSacralSlope,
            standingSacralSlope: aPlanDetails.standingSacralSlope,
            standingLumbarLordosis: aPlanDetails.standingLumbarLordosis,
            sittingLumbarLordosis: aPlanDetails.sittingLumbarLordosis,
            cupInclinationAngle: aPatientUtils.cupInclination() * 180.0 / Math.PI,
            cupAnteversionAngle: aPatientUtils.cupVersion() * 180.0 / Math.PI,
            stemAnteversionAngle: StemHeadUtilities.isValidStem(theContext.engineering.stemInfo)
                ? aPatientUtils.stemVersion() * 180.0 / Math.PI
                : null,
            combinedAnteversionAngle: StemHeadUtilities.isValidStem(theContext.engineering.stemInfo)
                ? aPatientUtils.combinedVersion() * 180.0 / Math.PI
                : null,
            referencePlane: aPlanDetails.referencePlane,
            legLengthPreop: aPatientUtils.legLengthVsPreOp / aUnitsScale,
            legLengthOpposite: aPatientUtils.legLengthVsOpposite / aUnitsScale,
            legOffsetPreop: aPatientUtils.legOffsetVsPreOp / aUnitsScale,
            legOffsetOpposite: aPatientUtils.legOffsetVsOpposite / aUnitsScale,
            corShiftSuperiorPreop: aPatientUtils.corShift.preOp.superior / aUnitsScale,
            corShiftMedialPreop: aPatientUtils.corShift.preOp.medial / aUnitsScale,
            corShiftPosteriorPreop: aPatientUtils.corShift.preOp.posterior / aUnitsScale,
            corShiftSuperiorOpposite: aPatientUtils.corShift.opposite.superior / aUnitsScale,
            corShiftMedialOpposite: aPatientUtils.corShift.opposite.medial / aUnitsScale,
            corShiftPosteriorOpposite: aPatientUtils.corShift.opposite.posterior / aUnitsScale,
            neckResectionDepth: StemHeadUtilities.isValidStem(theContext.engineering.stemInfo)
                ? aPatientUtils.neckCutHeight() / aUnitsScale
                : null
        };
    }
}
